import React, { createContext, useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import { clearColorPreferenceCache } from '../hooks/useColorPreferences';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [setupRequired, setSetupRequired] = useState(false);
  const logoutTriggeredRef = useRef(false);

  useEffect(() => {
    // Check if setup is required (no users exist) and if user is logged in
    const checkInitialState = async () => {
      try {
        // First check if setup is required
        const setupResponse = await fetch(`${API_URL}/api/auth/setup/status`);
        if (setupResponse.ok) {
          const setupData = await setupResponse.json();
          if (setupData.setup_required) {
            setSetupRequired(true);
            setLoading(false);
            return; // Don't check for user session if setup is required
          }
        }
      } catch (err) {
        // If we can't reach the server, continue with normal flow
        console.error('Could not check setup status:', err);
      }

      // Check if user is already logged in
      const token = localStorage.getItem('auth_token');
      if (token) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setCurrentUser(data.user);
          } else {
            localStorage.removeItem('auth_token');
          }
        } catch (err) {
          localStorage.removeItem('auth_token');
        }
      }
      setLoading(false);
    };

    checkInitialState();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      if (!response.ok) {
        const responseText = await response.text();

        // Track login failure
        if (window.posthog) {
          window.posthog.capture('login_failed', {
            username,
            reason: responseText || 'Unknown error'
          });
        }

        throw new Error(responseText || 'Login failed');
      }

      const data = await response.json();
      localStorage.setItem('auth_token', data.token);
      setCurrentUser(data.user);
      setError(null);

      // Track successful login
      if (window.posthog) {
        window.posthog.capture('user_logged_in', {
          user_role: data.user.role,
          has_teams: (data.user.teams?.length || 0) > 0,
        });
      }

      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      // Track logout before clearing state
      if (window.posthog) {
        window.posthog.capture('user_logged_out');
      }

      if (token) {
        await fetch(`${API_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('auth_token');
      clearColorPreferenceCache();
      setCurrentUser(null);
      setLoading(false);
    }
  };


  // frontend/src/context/AuthContext.js
  const authFetch = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    const startTime = performance.now();

    // Set up headers with Authorization but don't use credentials: 'include'
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    // Only set Content-Type if explicitly provided or if body is not FormData
    // For FormData, browser must set Content-Type automatically with boundary
    // Don't set Content-Type for GET requests (file downloads need original Content-Type from server)
    const method = options.method?.toUpperCase() || 'GET';
    if (options.headers?.['Content-Type']) {
      headers['Content-Type'] = options.headers['Content-Type'];
    } else if (!(options.body instanceof FormData) && method !== 'GET') {
      headers['Content-Type'] = 'application/json';
    }

    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers,
        mode: 'cors',
        // Remove credentials: 'include' since we're using Authorization header
      });

      // Track API performance
      const duration = performance.now() - startTime;
      if (window.posthog) {
        window.posthog.capture('api_request', {
          endpoint: url.replace(API_URL, ''),
          method: method,
          status: response.status,
          duration_ms: Math.round(duration),
          success: response.ok,
        });
      }

      if (response.status === 401) {
        // Only trigger logout if we have an active session (avoid clearing fresh login tokens)
        const currentToken = localStorage.getItem('auth_token');
        if (!logoutTriggeredRef.current && currentToken && currentToken === token) {
          logoutTriggeredRef.current = true;
          console.error('Session expired, logging out');
          localStorage.removeItem('auth_token');
          setCurrentUser(null);
          // Reset after short delay so future real 401s still work
          setTimeout(() => { logoutTriggeredRef.current = false; }, 2000);
        }
        throw new Error('Sitzung abgelaufen. Bitte erneut anmelden.');
      }

      return response;
    } catch (error) {
      // Track API errors
      const duration = performance.now() - startTime;
      if (window.posthog) {
        window.posthog.capture('api_error', {
          endpoint: url.replace(API_URL, ''),
          method: method,
          error: error.message,
          duration_ms: Math.round(duration),
        });
      }

      // Better error logging for CORS issues
      if (error.message.includes('CORS')) {
        console.error('CORS Error:', error.message);
        console.error('Request URL:', fullUrl);
        console.error('Request Method:', options.method || 'GET');
      } else {
        console.error('API request error:', error);
      }
      throw error;
    }
  }, []);
  // Get current token from localStorage
  const token = localStorage.getItem('auth_token');

  const value = useMemo(() => ({
    currentUser,
    loading,
    error,
    setupRequired,
    token,
    login,
    logout,
    authFetch
  }), [currentUser, loading, error, setupRequired, token, login, logout, authFetch]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);